import {useState, useEffect, useId} from "react";
import {
  Chip,
  IconButton,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import {AddCircle, CheckCircle} from "@mui/icons-material";
import {translate, truncateSx} from "@util";
import {Draggable} from "./Draggable";
import {DragPreview} from "./DragPreview";

export const chipSx = {
  ...truncateSx,
  width: 150,
  justifyContent: "space-between",
};

const parseValue = (value: unknown): string[] => {
  const list =
    typeof value === "string"
      ? value.split(",")
      : Array.isArray(value)
        ? value
        : [];
  return list.map(v => String(v).trim()).filter(v => v !== "");
};

const InputArray = (props: TextFieldProps) => {
  const {value, onChange, ...rest} = props;
  // tipo di drag univoco: i chip non possono finire in un'altra lista
  const dragType = `INPUT_ARRAY_${useId()}`;
  const [values, setValues] = useState<string[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [isNew, setIsNew] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValues(parseValue(value));
  }, [value]);

  const emitChange = (newValues: string[]) => {
    setValues(newValues);
    if (onChange) {
      onChange({
        target: {value: newValues.join(",")},
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const closeEdit = () => {
    setEditingIndex(null);
    setEditingValue("");
    setIsNew(false);
    setError(null);
  };

  const handleChipClick = (index: number) => {
    // una modifica non valida resta aperta finché non viene corretta
    if (editingIndex !== null) return;
    setEditingIndex(index);
    setEditingValue(values[index]);
    setIsNew(false);
  };

  const handleEditChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // la virgola è il separatore dei valori salvati
    setEditingValue(event.target.value.replace(/,/g, ""));
    setError(null);
  };

  const cancelEdit = () => {
    if (editingIndex === null) return;
    if (isNew) {
      setValues(values.filter((_, i) => i !== editingIndex));
    }
    closeEdit();
  };

  const confirmEdit = () => {
    if (editingIndex === null) return;
    const newValue = editingValue.trim();
    // valore vuoto: annulla la modifica (per eliminare c'è la X del chip)
    if (newValue === "") {
      cancelEdit();
      return;
    }
    if (values.some((v, i) => i !== editingIndex && v === newValue)) {
      setError(translate("error.validation.duplicate"));
      return;
    }
    if (isNew || newValue !== values[editingIndex]) {
      const newValues = [...values];
      newValues[editingIndex] = newValue;
      emitChange(newValues);
    }
    closeEdit();
  };

  const handleEditKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      // evita il submit del form
      event.preventDefault();
      confirmEdit();
    } else if (event.key === "Escape") {
      // evita la chiusura del dialog che contiene il form
      event.stopPropagation();
      cancelEdit();
    }
  };

  const handleDelete = (index: number) => {
    emitChange(values.filter((_, i) => i !== index));
  };

  const handleMove = (sourceIndex: number, targetIndex: number) => {
    const newValues = [...values];
    const [movedValue] = newValues.splice(sourceIndex, 1);
    newValues.splice(targetIndex, 0, movedValue);
    emitChange(newValues);
  };

  const handleAdd = () => {
    const newValues = [...values, ""];
    setValues(newValues);
    setEditingIndex(newValues.length - 1);
    setEditingValue("");
    setIsNew(true);
  };

  return (
    <Stack>
      <Typography>{translate(rest.label as string)}</Typography>
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} mt={1}>
        {values.map((val, index) =>
          editingIndex === index ? (
            <Stack key={index} direction="row" alignItems="center">
              <TextField
                {...rest}
                label={undefined}
                variant="standard"
                value={editingValue}
                onChange={handleEditChange}
                onKeyDown={handleEditKeyDown}
                onBlur={confirmEdit}
                error={error !== null}
                helperText={error}
                autoFocus
                size="small"
              />
              <IconButton
                // evita il blur del campo, che chiuderebbe la modifica prima del click
                onMouseDown={e => e.preventDefault()}
                onClick={confirmEdit}
                size="small"
                color="success"
                aria-label={translate("common.action.confirm")}>
                <CheckCircle />
              </IconButton>
            </Stack>
          ) : (
            <Draggable
              key={index}
              type={dragType}
              field={val}
              index={index}
              label={val}
              disabled={editingIndex !== null}
              onMove={handleMove}>
              <Chip
                size="small"
                label={val}
                onClick={() => handleChipClick(index)}
                onDelete={() => handleDelete(index)}
                sx={{...chipSx}}
              />
            </Draggable>
          ),
        )}
        <DragPreview type={dragType} />
        {editingIndex === null && (
          <IconButton
            onClick={handleAdd}
            size="small"
            aria-label={translate("common.action.add")}>
            <AddCircle />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

export {InputArray};
